import React, { useState, useEffect, useContext } from "react";
import classNames from "classnames/bind";
import {
  format,
  parseISO,
  isValid,
  eachDayOfInterval,
  isBefore,
  isAfter,
} from "date-fns";
import { vi } from "date-fns/locale";
import styles from "./GioVang.module.css";
import { ToastContext } from "~/components/context/ToastContext";
import { BASE_GIOVANG_URL } from "~/helper/apiService";
import images from "~/assets";

const cx = classNames.bind(styles);

function GioVang() {
  const { showToast } = useContext(ToastContext);

  const [formData, setFormData] = useState({
    phone: "",
    pickup: "",
    dropoff: "",
    date: "",
    time: "",
    passengers: "",
    note: "",
  });

  const [status, setStatus] = useState({
    loading: false,
    error: null,
    success: false,
  });

  const [modal, setModal] = useState({
    isOpen: false,
    promotionCount: null,
  });

  const [selectModalOpen, setSelectModalOpen] = useState(false);
  const [thankYouModalOpen, setThankYouModalOpen] = useState(false);
  const [availablePromotions, setAvailablePromotions] = useState([]);
  const [totalAvailable, setTotalAvailable] = useState(null);
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [minDate, setMinDate] = useState(null);
  const [maxDate, setMaxDate] = useState(null);
  const [dateOptions, setDateOptions] = useState([]);
  const [dateValue, setDateValue] = useState("");

  const validatePhone = (phone) =>
    /^(\+84|84|0)(3[2-9]|5[25689]|7[06-9]|8[1-9]|9[0-46-9])[0-9]{7}$/.test(
      phone
    );

  const formatDate = (dateString) => {
    if (!dateString || !isValid(parseISO(dateString))) return "";
    return format(parseISO(dateString), "dd/MM/yyyy", { locale: vi });
  };

  const normalizeDate = (dateString) => {
    if (!dateString || !isValid(parseISO(dateString))) return "";
    return format(parseISO(dateString), "yyyy-MM-dd");
  };

  const generateDateOptions = (startDate, endDate) => {
    if (
      !startDate ||
      !endDate ||
      !isValid(parseISO(startDate)) ||
      !isValid(parseISO(endDate))
    )
      return [];
    const start = parseISO(startDate);
    const end = parseISO(endDate);
    const dates = eachDayOfInterval({ start, end });
    return dates.map((date) => ({
      value: format(date, "yyyy-MM-dd"),
      label: format(date, "dd/MM/yyyy", { locale: vi }),
    }));
  };

  const fetchPromotions = async () => {
    try {
      const response = await fetch(`${BASE_GIOVANG_URL}promotions`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      const data = await response.json();
      if (response.ok) {
        console.log("data: ", data)
        const available = data.promotions.filter((p) => {
          const dateEnd = parseISO(p.date_end);
          if (!isValid(dateEnd)) {
            console.error(
              `Invalid date_end for promotion ${p.id}: ${p.date_end}`
            );
            return false;
          }
          return p.count > 0 // && !isBefore(dateEnd, new Date());
        });
        console.log("Available promotions:", available);
        setAvailablePromotions(available);
        const total = available.reduce((acc, p) => acc + p.count, 0);
        setTotalAvailable(total);

        if (total === 0) {
          setModal({ isOpen: true, promotionCount: 0 });
        } else if (!thankYouModalOpen) {
          setSelectModalOpen(true);
        }
      } else {
        showToast(data.error || "Không thể lấy thông tin khuyến mãi!", "error");
      }
    } catch (err) {
      showToast("Lỗi kết nối: " + err.message, "error");
    }
  };

  useEffect(() => {
    fetchPromotions();
  }, [showToast]);

  useEffect(() => {
    if (minDate && maxDate) {
      setDateOptions(generateDateOptions(minDate, maxDate));
    } else {
      setDateOptions([]);
    }
  }, [minDate, maxDate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "date") {
      const normalizedDate = normalizeDate(value);
      console.log("Selected date (raw):", value);
      console.log("Selected date (normalized):", normalizedDate);
      setFormData((prev) => ({ ...prev, [name]: normalizedDate }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSelectPromotion = (promotion) => {
    setSelectedPromotion(promotion);
    setMinDate(promotion.date_start);
    setMaxDate(promotion.date_end);
    setFormData((prev) => ({ ...prev, date: "" }));
    setSelectModalOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedPromotion) {
      showToast("Vui lòng chọn một voucher trước khi gửi!", "error");
      setSelectModalOpen(true);
      return;
    }

    if (!validatePhone(formData.phone)) {
      setStatus({
        loading: false,
        error: "Số điện thoại không hợp lệ!",
        success: false,
      });
      showToast("Số điện thoại không hợp lệ!", "error");
      return;
    }

    const travelDate = parseISO(formData.date);
    const startDate = parseISO(minDate);
    const endDate = parseISO(maxDate);

    console.log("Kiểm tra ngày:", {
      travelDate: format(travelDate, "yyyy-MM-dd"),
      startDate: format(startDate, "yyyy-MM-dd"),
      endDate: format(endDate, "yyyy-MM-dd"),
    });

    if (isBefore(travelDate, startDate) || isAfter(travelDate, endDate)) {
      setStatus({
        loading: false,
        error: `Ngày đi phải nằm trong khoảng từ ${formatDate(minDate)} đến ${formatDate(maxDate)}`,
        success: false,
      });
      showToast(
        `Ngày đi phải nằm trong khoảng từ ${formatDate(minDate)} đến ${formatDate(maxDate)}`,
        "error"
      );
      return;
    }

    const payload = {
      phone: formData.phone,
      pickup: formData.pickup,
      dropoff: formData.dropoff,
      travel_date: formData.date,
      travel_time: formData.time.replace("h", ":00:00"),
      passengers: formData.passengers,
      note: formData.note || null,
      promotion_id: selectedPromotion.id,
    };
    console.log("Payload trước khi gửi:", payload);

    try {
      setStatus({ loading: true, error: null, success: false });

      const response = await fetch(`${BASE_GIOVANG_URL}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include",
      });

      const data = await response.json();
      if (response.ok) {
        setDateValue(formData.date);
        setStatus({ loading: false, error: null, success: true });
        showToast(data.message || "Hệ thống đã ghi nhận đăng ký của bạn!", "success");
        setSelectModalOpen(false);
        setThankYouModalOpen(true);
        setFormData({
          phone: "",
          pickup: "",
          dropoff: "",
          date: "",
          time: "",
          passengers: "",
          note: "",
        });
        setSelectedPromotion(null);
        setMinDate(null);
        setMaxDate(null);
        setDateOptions([]);
        await fetchPromotions();
      } else {
        setStatus({
          loading: false,
          error: data.error || "Nhận khuyến mãi thất bại!",
          success: false,
        });
        showToast(data.error || "Nhận khuyến mãi thất bại!", "error");
      }
    } catch (err) {
      setStatus({
        loading: false,
        error: "Lỗi kết nối: " + err.message,
        success: false,
      });
      showToast("Lỗi kết nối: " + err.message, "error");
    }
  };

  const closeModal = () => {
    setModal((prev) => ({ ...prev, isOpen: false }));
  };

  const closeThankYouModal = () => {
    setThankYouModalOpen(false);
  };

  const timeOptions = [
    "4h",
    "5h",
    "6h",
    "7h",
    "8h",
    "9h",
    "10h",
    "11h",
    "12h",
    "13h",
    "14h",
    "15h",
    "16h",
    "17h",
    "18h",
    "19h",
    "20h",
    "21h",
    "22h",
    "23h",
  ];

  const passengerOptions = ["1", "2", "3", "4", "Bao Xe"];

  return (
    <div className={cx("section")}>
      {modal.isOpen && (
        <div
          className={cx("modal-overlay", {
            "no-close": modal.promotionCount === 0,
          })}
        >
          <div className={cx("modal")} onClick={(e) => e.stopPropagation()}>
            {modal.promotionCount !== 0 && (
              <button className={cx("modal-close")} onClick={closeModal}>
                ✕
              </button>
            )}
            <h3 className={cx("modal-title")}>Thông tin khuyến mãi</h3>
            <p className={cx("modal-content")}>
              {modal.promotionCount === null
                ? "Đang tải thông tin khuyến mãi..."
                : "Khuyến mãi đã hết! Xin mời quý khách quay lại vào khung giờ vàng tuần sau."}
            </p>
          </div>
        </div>
      )}

      {selectModalOpen && !thankYouModalOpen && (
        <div className={cx("modal-overlay")}>
          <div className={cx("modal")}>
            <button
              className={cx("modal-close")}
              onClick={() => setSelectModalOpen(false)}
            >
              ✕
            </button>
            <h3 className={cx("modal-title")}>Chọn Loại Voucher</h3>
            <div className={cx("voucher-list")}>
              {availablePromotions.map((p) => (
                <div key={p.id} className={cx("voucher-item")}>
                  <p>{p.type}</p>
                  <p>Còn lại: {p.count}</p>
                  <p>
                    Thời gian: {formatDate(p.date_start)} -{" "}
                    {formatDate(p.date_end)}
                  </p>
                  <button
                    onClick={() => handleSelectPromotion(p)}
                    className={cx("select-btn")}
                    disabled={status.loading}
                  >
                    Chọn
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {thankYouModalOpen && (
        <div className={cx("modal-overlay")}>
          <div className={cx("modal")}>
            <h3 className={cx("modal-title")}>Cảm ơn quý khách!</h3>
            <p className={cx("modal-content")}>
              Xanh19 đã ghi nhận thông tin vé của Quý khách. Danh sách khách hàng đặt vé sớm sẽ được công bố trên nhóm, đồng thời nhân viên Xanh19 sẽ chủ động liên hệ để xác nhận
            </p>
          </div>
        </div>
      )}

      <div className={cx("container")}>
        <div className={cx("header")}>
          <div className={cx("header-content")}>
            <div className={cx("logo-container")}>
              <img
                src={images.logoGioVang}
                alt="Giờ Vàng Xanh 19 Logo"
                className={cx("logo")}
              />
            </div>
            <div className={cx("header-text")}>
              <h1 className={cx("title")}>Giờ Vàng Xanh 19</h1>
              <h2 className={cx("route")}>Việt Trì ⇔ Hà Nội</h2>
              <p
                className={cx("promotion-info", { zero: totalAvailable === 0 })}
              >
                {totalAvailable === null
                  ? "Đang tải thông tin khuyến mãi..."
                  : totalAvailable === 0
                    ? "Khuyến mãi đã hết"
                    : `Còn ${totalAvailable} khuyến mãi`}
              </p>
              <p className={cx("contact")}>
                Hotline: <a href="tel:0843348888">0843.34.8888</a>
              </p>
            </div>
          </div>
        </div>

        {selectedPromotion && (
          <div className={cx("selected-promotion")}>
            <h3>Voucher đã chọn: {selectedPromotion.type}</h3>
            <p>
              Thời gian áp dụng: {formatDate(selectedPromotion.date_start)} -{" "}
              {formatDate(selectedPromotion.date_end)}
            </p>
            <button
              onClick={() => setSelectModalOpen(true)}
              className={cx("change-voucher-btn")}
            >
              Đổi voucher
            </button>
          </div>
        )}

        {selectedPromotion && (
          <form className={cx("form")} onSubmit={handleSubmit}>
            <div className={cx("form-group")}>
              <label htmlFor="phone-input">Số điện thoại</label>
              <input
                id="phone-input"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Số điện thoại"
                aria-label="Số điện thoại"
                required
                pattern="^(\+84|84|0)(3[2-9]|5[25689]|7[06-9]|8[1-9]|9[0-46-9])[0-9]{7}$"
              />
            </div>

            <div className={cx("form-group")}>
              <label htmlFor="pickup-input">Điểm Đón</label>
              <textarea
                id="pickup-input"
                name="pickup"
                value={formData.pickup}
                onChange={handleChange}
                placeholder="Nhập Điểm Đón"
                required
                aria-label="Điểm Đón"
              />
            </div>

            <div className={cx("form-group")}>
              <label htmlFor="dropoff-input">Điểm Trả</label>
              <textarea
                id="dropoff-input"
                name="dropoff"
                value={formData.dropoff}
                onChange={handleChange}
                placeholder="Nhập Điểm Trả"
                required
                aria-label="Điểm Trả"
              />
            </div>

            <div className={cx("form-group")}>
              <label htmlFor="date-input">Ngày Đi</label>
              <select
                id="date-input"
                name="date"
                value={formData.date}
                onChange={handleChange}
                aria-label="Ngày Đi"
                required
              >
                <option value="" disabled>
                  Chọn ngày đi
                </option>
                {dateOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {minDate && maxDate && (
                <p className={cx("date-info")}>
                  Vui lòng chọn ngày từ {formatDate(minDate)} đến{" "}
                  {formatDate(maxDate)}
                </p>
              )}
              {dateOptions.length === 0 && selectedPromotion && (
                <p className={cx("date-error")}>
                  Không có ngày khả dụng cho voucher này. Vui lòng chọn voucher
                  khác.
                </p>
              )}
            </div>

            <div className={cx("form-group")}>
              <label htmlFor="time-input">Giờ Đi</label>
              <select
                id="time-input"
                name="time"
                value={formData.time}
                onChange={handleChange}
                aria-label="Giờ Đi"
                required
              >
                <option value="" disabled>
                  Chọn giờ đi
                </option>
                {timeOptions.map((time) => (
                  <option key={time} value={time}>
                    {time}
                  </option>
                ))}
              </select>
            </div>

            <div className={cx("form-group")}>
              <label htmlFor="passengers-input">Số Người Đi</label>
              <select
                id="passengers-input"
                name="passengers"
                value={formData.passengers}
                onChange={handleChange}
                aria-label="Số Người Đi"
                required
              >
                <option value="" disabled>
                  Chọn số người
                </option>
                {passengerOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div className={cx("form-group")}>
              <label htmlFor="note-input">Ghi Chú (Nếu Có)</label>
              <textarea
                id="note-input"
                name="note"
                value={formData.note}
                onChange={handleChange}
                placeholder="Ghi chú thêm"
                aria-label="Ghi Chú"
              />
            </div>

            {status.error && <p className={cx("error")}>{status.error}</p>}
            {status.success && (
              <p className={cx("success")}>Hệ thống đã ghi nhận đăng ký của bạn!</p>
            )}

            <button
              type="submit"
              className={cx("submit-btn", { loading: status.loading })}
              disabled={status.loading || !formData.date || !selectedPromotion}
            >
              {status.loading ? (
                <>
                  <span className={cx("loader")}></span>
                  Đang xử lý...
                </>
              ) : (
                "Gửi"
              )}
            </button>
          </form>
        )}

        <div className={cx("info")}>
          <h3 className={cx("info-title")}>Xanh 19</h3>
          <p className={cx("info-desc")}>Xe ghép Việt - Trì Hà Nội</p>

          <div className={cx("pricing")}>
            <h4>Giá Vé thông thường</h4>
            <p>Thứ 2 - Thứ 5: 150k</p>
            <p>Thứ 6 - Chủ Nhật: 180k</p>
            <p>Bao Xe:</p>
            <ul>
              <li>Thứ 2 - Thứ 5: 500k</li>
              <li>Thứ 6 - Chủ Nhật: 600k</li>
            </ul>
            <p className={cx("route-info")}>
              Việt Trì ⇔ Hà Nội - Đưa Đón Tận Nơi
            </p>
          </div>

          <div className={cx("features")}>
            <h4>Đặc điểm nổi bật</h4>
            <ul>
              <li>Giá hợp lý – Dịch vụ tận tâm</li>
              <li>Xe điện sang trọng – Trải nghiệm êm ái</li>
              <li>Nhận khuyến mãi nhanh chóng, thanh toán tiện lợi</li>
              <li>Đúng giờ – Không lo trễ hẹn</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GioVang;
